import { useState } from "react";
import { AuthImg } from "@/components/AuthImg";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { PhotoFixAction, PhotoHistory } from "@/components/PhotoFixAction";
import { MainPhotoAction } from "@/components/MainPhotoAction";
import { ImageOff, Archive, RotateCcw, EyeOff, ImagePlus, Star } from "lucide-react";

// Thumbnail grid. Tapping a photo opens THE one viewer — zoom, rotate, drag to pan, paging, touch
// (his instruction, 2026-10-15: the truck page is this grid, and it used to open a zoom toggle and
// nothing else). photos = [{file_id, is_archived}, ...]
// canManage + onArchive/onRestore enable archive (not delete) controls. View side is read-only.
// onAttachToPart(file_id) (optional) renders a per-photo "add to a part" action (donor check-in photos).
// fixScope + fixRecordId mount the ONE shared correction action (reason, move, undo) instead of
// the old bare archive button — same action as the floor cards and the photo viewer.
export function PhotoGallery({ photos = [], testid = "photo-gallery", canManage = false, onArchive, onRestore, onAttachToPart, showKind = false,
                               fixScope, fixRecordId, onFixed,
                               mainScope = "donor", mainRecordId, mainPhotoFileId, resolvedMainFileId, onMainChanged }) {
  const [view, setView] = useState(null);   // { list: [photo,...], idx }
  const [showArchived, setShowArchived] = useState(false);
  const [confirmId, setConfirmId] = useState(null);

  const all = (photos || []).filter((p) => p && p.file_id);
  const activePhotos = all.filter((p) => !p.is_archived);
  const archivedPhotos = all.filter((p) => p.is_archived);

  const openAt = (list, idx) => setView({ list, idx });

  const doArchive = async () => {
    const id = confirmId;
    setConfirmId(null);
    if (id && onArchive) await onArchive(id);
  };

  return (
    <>
      {activePhotos.length === 0 ? (
        <div data-testid={`${testid}-empty`} className="flex items-center gap-2 text-sm text-slate-400 py-6 justify-center">
          <ImageOff className="w-4 h-4" /> No photos yet
        </div>
      ) : (
        <div data-testid={testid} className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
          {activePhotos.map((p, i) => (
            <div key={p.file_id} className="relative group aspect-square">
              <button data-testid={`${testid}-thumb-${i}`} onClick={() => openAt(activePhotos, i)}
                className="w-full h-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 hover:border-[#E0781A] hover:shadow transition-all">
                <AuthImg fileId={p.file_id} thumb missing={!!p.bytes_missing} className="w-full h-full object-cover" alt={`photo ${i + 1}`} />
              </button>
              {showKind && (() => {
                const dismantle = p.photo_kind === "pool" || !!p.session_id;
                return (
                  <span data-testid={`${testid}-kind-${i}`}
                    className={`absolute bottom-1 left-1 text-[8px] font-mono uppercase px-1 py-0.5 rounded pointer-events-none ${dismantle ? "bg-[#E0781A] text-white" : "bg-slate-700/85 text-white"}`}>
                    {dismantle ? "Dismantle" : "Check-in"}
                  </span>
                );
              })()}
              {resolvedMainFileId === p.file_id && (
                <span data-testid={`${testid}-is-main-${i}`}
                  title={`${mainPhotoFileId === p.file_id ? "Chosen to show" : "Shown by default for"} this ${mainScope === "part" ? "part" : "truck"}`}
                  className="absolute bottom-1 right-1 inline-flex items-center gap-0.5 text-[8px] font-mono uppercase px-1 py-0.5 rounded bg-[#E0781A] text-white pointer-events-none">
                  <Star className="w-2 h-2 fill-current" /> Main
                </span>
              )}
              {fixScope && fixRecordId ? (
                <div className="absolute top-1 right-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-all">
                  {mainRecordId && (
                    <MainPhotoAction scope={mainScope} recordId={mainRecordId} fileId={p.file_id} compact
                      isMain={resolvedMainFileId === p.file_id}
                      isOverride={!!mainPhotoFileId && mainPhotoFileId === p.file_id}
                      onChanged={onMainChanged} testid={`${testid}-main-${i}`} />
                  )}
                  <PhotoFixAction scope={fixScope} recordId={fixRecordId} photo={p} compact
                    testid={`${testid}-fix-${i}`} onDone={onFixed} />
                </div>
              ) : canManage && onArchive && (
                <button data-testid={`${testid}-archive-${i}`} onClick={(e) => { e.stopPropagation(); setConfirmId(p.file_id); }}
                  title="Archive photo"
                  className="absolute top-1 right-1 inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/90 border border-slate-200 text-slate-600 opacity-0 group-hover:opacity-100 hover:bg-[#B01E28] hover:text-white hover:border-[#B01E28] transition-all">
                  <Archive className="w-3.5 h-3.5" />
                </button>
              )}
              {onAttachToPart && (
                <button data-testid={`${testid}-attach-${i}`} onClick={(e) => { e.stopPropagation(); onAttachToPart(p.file_id); }}
                  title="Add this check-in photo to a part"
                  className="absolute top-1 left-1 inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/90 border border-slate-200 text-slate-600 opacity-0 group-hover:opacity-100 hover:bg-[#E0781A] hover:text-white hover:border-[#E0781A] transition-all">
                  <ImagePlus className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {/* REMOVED PHOTOS — kept, never deleted, off by default, and each one says who took it off
          and why. A photo that keeps bouncing means two people disagree about whether it belongs. */}
      {(canManage || (fixScope && fixRecordId)) && archivedPhotos.length > 0 && (
        <div className="mt-3">
          <button data-testid={`${testid}-toggle-archived`} onClick={() => setShowArchived((v) => !v)}
            className="inline-flex items-center gap-1.5 text-xs font-mono text-slate-500 hover:text-slate-800 transition-colors">
            <EyeOff className="w-3.5 h-3.5" />
            {showArchived ? "Hide" : "Show"} archived ({archivedPhotos.length})
          </button>
          {showArchived && (
            <div data-testid={`${testid}-archived`} className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mt-2">
              {archivedPhotos.map((p, i) => (
                <div key={p.file_id} className="group">
                  <div className="relative aspect-square">
                    <button onClick={() => openAt(archivedPhotos, i)}
                      className="w-full h-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 opacity-60 hover:opacity-100 transition-all">
                      <AuthImg fileId={p.file_id} thumb missing={!!p.bytes_missing} className="w-full h-full object-cover grayscale" alt={`removed photo ${i + 1}`} />
                    </button>
                    <span className="absolute bottom-1 left-1 text-[8px] font-mono uppercase px-1 py-0.5 rounded bg-slate-800/80 text-white">removed</span>
                    {fixScope && fixRecordId ? (
                      <div className="absolute top-1 right-1">
                        <PhotoFixAction scope={fixScope} recordId={fixRecordId} photo={p} compact
                          testid={`${testid}-fix-archived-${i}`} onDone={onFixed} />
                      </div>
                    ) : onRestore && (
                      <button data-testid={`${testid}-restore-${i}`} onClick={(e) => { e.stopPropagation(); onRestore(p.file_id); }}
                        title="Restore photo"
                        className="absolute top-1 right-1 inline-flex items-center justify-center w-7 h-7 rounded-md bg-white/90 border border-slate-200 text-slate-600 opacity-0 group-hover:opacity-100 hover:bg-emerald-600 hover:text-white hover:border-emerald-600 transition-all">
                        <RotateCcw className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  <PhotoHistory photo={p} testid={`${testid}-history-${i}`} />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <PhotoLightbox photos={view?.list || []} index={view ? view.idx : null}
        onIndex={(i) => setView((v) => v && { ...v, idx: i })} onClose={() => setView(null)}
        testidPrefix={testid}
        fixScope={fixScope} fixRecordId={fixRecordId} onFixed={onFixed}
        mainScope={mainScope} mainRecordId={mainRecordId} mainPhotoFileId={mainPhotoFileId}
        resolvedMainFileId={resolvedMainFileId} onMainChanged={onMainChanged} />

      <AlertDialog open={!!confirmId} onOpenChange={(v) => !v && setConfirmId(null)}>
        <AlertDialogContent data-testid={`${testid}-archive-confirm`} className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Archive this photo?</AlertDialogTitle>
            <AlertDialogDescription>
              It will be hidden from the gallery but retained (never deleted) and can be restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`${testid}-archive-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid={`${testid}-archive-ok`} onClick={doArchive} className="bg-[#B01E28] hover:bg-[#8e1820]">Archive</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
